import { datadogLogs } from "@datadog/browser-logs";
import * as Sentry from "@sentry/react";
import {
  browserTracingIntegration,
  replayCanvasIntegration,
  replayIntegration,
  tanstackRouterBrowserTracingIntegration,
} from "@sentry/react";
import {
  type ContextData,
  type User,
  getDatadogConfig,
  getSentryConfig,
  isDevelopment,
} from "./monitoringConfig";

import type { ErrorEvent, EventHint } from "@sentry/react";
import type { AnyRouter } from "@tanstack/react-router";
import { isAxiosError } from "axios";

// Simplified severity levels
export enum MessageSeverity {
  Debug = "debug",
  Info = "info",
  Warning = "warning",
  Error = "error",
  Fatal = "fatal",
}

// Simplified log levels
export enum LogLevel {
  Debug = "debug",
  Info = "info",
  Warn = "warn",
  Error = "error",
}

// Re-export types
export type { ContextData, User };

// Get service configurations
export let sentryConfig = getSentryConfig();
export let datadogConfig = getDatadogConfig();

// Flags to track initialization state
let isSentryInitialized = false;
let isDatadogInitialized = false;

const DENIED_URLS = [
  // Third-party analytics
  /^https:\/\/api\.segment\.io/,
  // Static assets (scripts, styles, images, fonts, etc.)
  /\.(js|css|png|jpg|jpeg|gif|svg|woff|woff2|ttf|eot|ico)(\?.*)?$/,
];

// Per Sentry Replay docs, networkDetailAllowUrls matches the full absolute URL.
// Strings act as prefix matches, so normalized origins are sufficient.
const normalizeOrigin = (u: string) => u.replace(/\/+$/, "");

const getAllowedUrls = (): string[] => {
  const origins = new Set<string>();

  // Safety net to capture requests
  if (typeof window !== "undefined" && window.location?.origin) {
    origins.add(window.location.origin);
  }

  for (const u of [
    import.meta.env.GATEWAY_HOST,
    import.meta.env.FO_NEW_CHAT_HOST,
  ]) {
    if (u) {
      origins.add(String(u));
    }
  }

  return Array.from(origins).map(normalizeOrigin);
};

const ALLOWED_URLS = getAllowedUrls();

// The medical-coding encounter-time start/complete endpoints return 412
// (Precondition Failed) as an expected, handled state-machine signal when a
// clinician navigates away from / closes an encounter quickly. These are not
// real errors, so we drop them instead of reporting. This one endpoint was
// ~90% of the project's Sentry error volume.
const BENIGN_412_URL_PATTERN = /\/medical-coding\/encounter-time\//;

const UUID_PATTERN =
  /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

/**
 * Reduce an axios request URL to a stable path with dynamic ids replaced by
 * `:id`, so distinct resources group together for fingerprinting.
 */
function normalizeAxiosPath(rawUrl: string): string {
  let path = rawUrl;
  try {
    const base =
      typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "http://localhost";
    path = new URL(rawUrl, base).pathname;
  } catch {
    // rawUrl wasn't a parseable absolute/relative URL; use it as-is.
  }
  return path.replace(UUID_PATTERN, ":id").replace(/\/\d+(?=\/|$)/g, "/:id");
}

/**
 * Sentry `beforeSend` hook. Two jobs, both about the handled-AxiosError noise
 * that dominated this project's issues:
 *  1. Drop expected, handled 412s from the encounter-time endpoint.
 *  2. Fingerprint the remaining AxiosErrors by method + path + status. Every
 *     handled AxiosError shares the same axios-internal stacktrace, so Sentry
 *     otherwise collapses unrelated endpoints/statuses into one grab-bag issue.
 */
export function sentryBeforeSend(
  event: ErrorEvent,
  hint: EventHint,
): ErrorEvent | null {
  const error = hint?.originalException;

  if (isAxiosError(error)) {
    const status = error.response?.status;
    const url = error.config?.url ?? "";

    if (status === 412 && BENIGN_412_URL_PATTERN.test(url)) {
      return null;
    }

    if (status) {
      const method = error.config?.method?.toUpperCase() ?? "UNKNOWN";
      event.fingerprint = [
        "axios",
        method,
        normalizeAxiosPath(url),
        String(status),
      ];
    }
  }

  return event;
}

/**
 * Reset the monitoring state (for testing purposes)
 */
export function resetMonitoring(): void {
  isSentryInitialized = false;
  isDatadogInitialized = false;
  sentryConfig = getSentryConfig();
  datadogConfig = getDatadogConfig();
}

/**
 * Initialize monitoring services with configuration
 * @param router - Optional router instance for Sentry router integration
 */
export function initialize(router?: AnyRouter): void {
  // Initialize Sentry if enabled
  initializeSentry(router);
  // Initialize Datadog if enabled
  initializeDatadog();
}

/**
 * Initialize Sentry with configuration
 * @param router - Optional router instance for router integration
 */
function initializeSentry(router?: AnyRouter): void {
  // Skip if already initialized or explicitly disabled
  if (isSentryInitialized || !sentryConfig.enabled) {
    return;
  }

  const { dsn } = sentryConfig;
  if (!dsn && sentryConfig.enabled) {
    console.warn("Sentry DSN is missing. Sentry will not be initialized.");
    return;
  }

  // Build integrations array with proper typing
  const baseIntegrations = [
    browserTracingIntegration(),
    replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
      networkDetailDenyUrls: DENIED_URLS,
      networkDetailAllowUrls: ALLOWED_URLS,
      // Explicitly enable network body capture for matching URLs
      networkCaptureBodies: true,
      // Capture request and response headers for debugging
      networkRequestHeaders: ["Content-Type", "Accept", "Authorization"],
      networkResponseHeaders: ["Content-Type", "Content-Length"],
    }),
    replayCanvasIntegration(),
  ];

  // Add router integration only if router is provided
  const integrations = router
    ? [...baseIntegrations, tanstackRouterBrowserTracingIntegration(router)]
    : baseIntegrations;

  // Initialize Sentry
  Sentry.init({
    dsn,
    integrations,
    // Performance monitoring
    tracesSampleRate: sentryConfig.tracesSampleRate,
    // Session replay settings
    replaysSessionSampleRate: sentryConfig.replaysSessionSampleRate,
    replaysOnErrorSampleRate: sentryConfig.replaysOnErrorSampleRate,
    // Environment and release
    environment: sentryConfig.environment,
    release: sentryConfig.release,
    // Drop benign handled 412s and split the AxiosError grab-bag by endpoint.
    beforeSend: sentryBeforeSend,
  });

  isSentryInitialized = true;

  // Log initialization in development
  if (isDevelopment()) {
    console.info("Sentry initialized with:", {
      enabled: sentryConfig.enabled,
      environment: sentryConfig.environment,
    });
  }
}

/**
 * Initialize Datadog Logs
 */
function initializeDatadog(): void {
  // Skip if already initialized or explicitly disabled
  if (isDatadogInitialized || !datadogConfig.enabled) {
    return;
  }

  const { clientToken, service, env, version } = datadogConfig;
  if (!clientToken && datadogConfig.enabled) {
    console.warn(
      "Datadog client token is missing. Datadog will not be initialized.",
    );
    return;
  }

  // Initialize Datadog Logs
  datadogLogs.init({
    clientToken,
    service,
    env,
    version,
    forwardErrorsToLogs: true,
  });

  isDatadogInitialized = true;

  // Log initialization in development
  if (isDevelopment()) {
    console.info("Datadog initialized with:", {
      enabled: datadogConfig.enabled,
      environment: datadogConfig.env,
    });
  }

  logMessage("Datadog initialized", LogLevel.Info);
}

/**
 * Capture an error with optional context
 */
export function captureError(error: Error, context?: ContextData): void {
  // Send to Sentry if enabled
  if (sentryConfig.enabled) {
    Sentry.captureException(error, {
      extra: context,
    });
  }
}

/**
 * Capture a message with specified severity and optional context
 */
export function captureMessage(
  message: string,
  severity: MessageSeverity = MessageSeverity.Info,
  context?: ContextData,
): void {
  // Send to Sentry if enabled
  if (sentryConfig.enabled) {
    Sentry.captureMessage(message, {
      level: severity as Sentry.SeverityLevel,
      extra: context,
    });
  }
}

/**
 * Set user information for the current session
 */
export function setUser(user: User): void {
  // Set in Sentry if enabled
  if (sentryConfig.enabled) {
    Sentry.setUser(user);
  }
}

/**
 * Clear user information
 */
export function clearUser(): void {
  // Clear in Sentry if enabled
  if (sentryConfig.enabled) {
    Sentry.setUser(null);
  }
}

/**
 * Add a breadcrumb to track user actions
 */
export function addBreadcrumb(breadcrumb: {
  category?: string;
  message: string;
  data?: ContextData;
  level?: MessageSeverity;
}): void {
  // Add to Sentry if enabled
  if (sentryConfig.enabled) {
    Sentry.addBreadcrumb({
      category: breadcrumb.category,
      message: breadcrumb.message,
      data: breadcrumb.data,
      level: breadcrumb.level as Sentry.SeverityLevel,
    });
  }
}

export function logMessage(
  message: string,
  level: LogLevel = LogLevel.Info,
  additionalData?: ContextData,
): void {
  if (datadogConfig.enabled) {
    datadogLogs.logger[level](message, additionalData);
  }
}

/**
 * Get the current Sentry replay ID
 */
export function getReplayId(): string | undefined {
  if (sentryConfig.enabled) {
    return Sentry.getReplay()?.getReplayId();
  }
  return undefined;
}

// Export Sentry's ErrorBoundary for React components
export { ErrorBoundary } from "@sentry/react";
